import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import * as Sentry from '@sentry/react';

import { authActions, authSelector } from './index';
import { toastActions } from '../toast';
import { Api } from 'api';
import {
  _confirm,
  addAccount,
  changeAccount,
  firebaseSetUserId,
  getAccounts,
  getDeviceId,
  getRandomInt,
  getRegistrationToken,
  getStorageItem,
  getTargetModal,
  getTelegramBotId,
  getToken,
  getUtmLocal,
  getUtmOffline,
  isIsolated,
  removeAccount,
  removeRegistrationToken,
  removeStorageItem,
  removeToken,
  sendEvent,
  setRegistrationToken,
  setToken,
  statReachGoal,
  validateEmail,
  validatePhone,
} from '$utils';
import router from 'navigation/router';
import {
  AuthWithPasswordAction,
  ChangeAccountAction,
  CreateAccountAction,
  EmailAuthAction,
  GoogleAuthAction,
  LogoutAction,
  RegistrationUploadPhotoAction,
  SendAuthCodeAction,
  TelegramAuthAction,
  TelegramOidcAuthAction,
  TelegramRequestLinkAction,
  TelegramLinkAuthAction,
  ValidateEmailCodeAction,
} from './interface';
import { mainActions } from '../main';
import {
  ADD_ACCOUNT,
  AUTH,
  AUTH_EMAIL,
  AUTH_REQUIRED,
  BIND_EMAIL,
  DASHBOARD,
  FEED,
  GOOGLE_AUTH,
  MAIN,
  REGISTRATION,
} from '$shared/constants/pages';
import { realtimeActions } from '$store/realtime';
import { batchActions } from '$store';
import { t } from '$translation/helper';
import { postsActions } from '$store/posts';
import { PostType } from '$store/models';
import { ContentPriorityType } from '$store/main/interface';
import { showModal } from '$navigation/helper';
import { promoCodeSelector } from '$store/promoCode';
import { notesActions } from '$store/notes';
import { passkeyActions } from '$store/passkey';
import { paymentMethodsActions } from '$store/paymentMethods';
import { imActions } from '$store/im';

function* sendNotifyTracking() {
  try {
    const data = getStorageItem('notifyTracking');
    if (data) {
      removeStorageItem('notifyTracking');
      const exp = data.split(',').map((item) => parseInt(item, 10));
      yield call(Api.post, '/main/notify', {
        type: exp[0],
        extra: exp[1],
      });
    }
  } catch (e) {}
}

export function* initWorker() {
  try {
    const tokenInfo = yield call(getToken);

    yield put(authActions.reloadAccounts());

    if (tokenInfo.userId > 0) {
      const data = yield call(Api.get, '/main/init');
      yield put(
        batchActions(
          mainActions.setOffline(false),
          mainActions.setIncognitoEnabled(data.user.incognito),
          mainActions.setUser(data.user),
          paymentMethodsActions.setCards(data.cards),
          mainActions.setPermissions(data.permissions),
          passkeyActions.setRegistered(data.user.passKey),
          authActions.setLogged(data.user.id),
          mainActions.setNewDialogs(data.newDialogs),
          mainActions.setPayInfo({
            firstName: data.payFirstName,
            lastName: data.payLastName,
            email: data.payEmail,
            phone: data.payPhone,
          }),
          mainActions.setCountryCode(data.countryCode),
          mainActions.setHasNotify(data.hasNotify),
          mainActions.setHasSubscription(data.hasSubscription),
          mainActions.setHasPersonalSubscription(data.hasPersonalSubscription),
          mainActions.setHasPassword(data.hasPassword),
          mainActions.setTopUpAddress(data.topUpAddress),
          mainActions.setSubscriptionPrice({
            ton: data.subscriptionPrice,
            rubles: data.subscriptionRubPrice,
          }),
          mainActions.setDecryptKeyLegacy(data.decryptKey),
          mainActions.setDecryptKeys(data.decryptKeys),
          mainActions.setBalance(data.balance),
          mainActions.setEurBalance(data.eurBalance),
          mainActions.setPlaceBalance(data.placeBalance),
          mainActions.setMVerified(data.mVerified),
          mainActions.setHasBot(data.hasBot),
          mainActions.setGenderFilter(data.genderFilter),
          postsActions.updateFormType({
            formId: `post_${data.user.id}`,
            type: data.mVerified ? PostType.SUBSCRIPTION : PostType.FREE,
          }),
          mainActions.setCanWithdraw(data.canWithdraw),
          mainActions.setContentPriority(data.contentPriority),
          mainActions.setSubscriptionsPrices({
            adult: data.adultSubscriptionPrice,
            other: data.otherSubscriptionPrice,
          }),
          mainActions.setIsWatermarkDisabled(data.isWatermarkDisabled),
          mainActions.setCanUseSponsorSubscription(data.canUseSponsorSubscription),
          mainActions.setTonAddress(data.tonAddress),
          mainActions.setCanBroadcastMessages(data.canBroadcastMessages),
          mainActions.setEurTopUpFee(data.eurTopUpFee || 0),
          mainActions.setFeePercent(data.feePercent),
          mainActions.setPriceLimits(data.priceLimits || {}),
          mainActions.setShowFeatures(data.showFeatures),
          mainActions.setFraudAccepted(data.isFraudTermsAccepted),
          mainActions.setManagerLink(data.personalManagerLink || ''),
          mainActions.setIsModer(!!data.isModer),
          mainActions.setCryptoEnabled(!!data.isCryptoEnabled),
          mainActions.setBlackFriday2025Enabled(!!data.isBlackFriday2025Enabled),
          mainActions.setShouldBindEmail(!!data.shouldBindEmail),
        ),
      );

      sendEvent('session_started');

      yield call(firebaseSetUserId, data.user.id);
      yield call(Sentry.setUser, {
        id: String(data.user.id),
        email: data.user.email || undefined,
        username: data.user.domain || undefined,
      });

      let isAdult = false;
      if (data.user.verified !== 2) {
        if (data.decryptKeys[-1]) {
          statReachGoal('adult_user');
          isAdult = true;
        } else if (data.contentPriority === ContentPriorityType.ADULT) {
          statReachGoal('free_adult_user');
          isAdult = true;
        }
      }

      if (isAdult) {
        statReachGoal('any_adult_user');

        if (data.user.id % 2 === 0) {
          statReachGoal('even_adult_user');
        } else {
          statReachGoal('odd_adult_user');
        }
      } else {
        statReachGoal('any_media_user');

        if (data.decryptKeys[-2]) {
          statReachGoal('media_user');
        } else if (data.contentPriority === ContentPriorityType.OTHER) {
          statReachGoal('free_media_user');
        }
      }

      //if (location.pathname === '/' || location.pathname === '/registration') {
      if (location.pathname === '/') {
        if (data.user.verified === 2) {
          router.navigate(DASHBOARD, {}, { replace: true });
        } else {
          router.navigate(FEED, {}, { replace: true });

          // if (data.user.id % 2 === 0) {
          //   router.navigate(FEED, {}, { replace: true });
          // } else {
          //   router.navigate(REELS, {}, { replace: true });
          // }
        }
      }

      if (
        data.shouldBindEmail &&
        data.canWithdraw &&
        !sessionStorage.getItem('bindEmailShown') &&
        !location.pathname.startsWith('/email/bind/')
      ) {
        sessionStorage.setItem('bindEmailShown', '1');
        router.navigate(BIND_EMAIL, {}, { replace: true });
      }

      // if (
      //   data.contentPriority === ContentPriorityType.OTHER &&
      //   location.hostname === 'ton.place'
      // ) {
      //   yield call(migrateToMediaDomain);
      // }

      yield put(realtimeActions.listen(tokenInfo.token));
      yield put(imActions.loadServiceAccounts());
      // yield call(startVisibilityWatcher);
      yield call(statReachGoal, 'user_visit');
      if (data.user.verified === 2) {
        yield call(statReachGoal, 'model_visit');
      }
      yield call(addAccount, {
        id: tokenInfo.userId,
        token: tokenInfo.token,
        updatedAt: Date.now(),
      });

      // if (isAdult) {
      //   const { isNotifyEnabled } = yield select(mainSelector);
      //   const canShowNotifyReq = yield call(canShowNotifyRequest);
      //   const canShowOnRoute = yield call(canShowNotifyOnRoute);
      //   const hasModal = yield call(isModalShown);
      //   if (
      //     !isNotifyEnabled &&
      //     canShowNotifyReq &&
      //     !hasModal &&
      //     canShowOnRoute &&
      //     location.pathname !== '/verification'
      //   ) {
      //     showModal('push_notifications');
      //   }
      // }

      yield fork(sendNotifyTracking);

      if (data.user.verified === 2) {
        yield put(notesActions.loadNotes());
      }
    } else {
      // if (location.pathname === '/registration') {
      //   location.href = '/';
      // } else {
      yield put(authActions.setLoading(false));
      // }
    }
  } catch (e) {
    if (e.isNetworkError) {
      yield put(mainActions.setOffline(true));
    } else if (e.serverError) {
      yield put(mainActions.setError('serverError'));
    }
    yield put(toastActions.setToastFail(e.message));
    // if (location.pathname === '/registration') {
    //   location.href = '/';
    // } else {
    yield put(authActions.setLoading(false));
    //}
  }
}

function migrateToMediaDomain() {
  const frame = document.createElement('iframe') as HTMLIFrameElement;
  frame.src = 'https://tonplace.io';
  document.body.appendChild(frame);
  frame.onload = () => {
    frame.contentWindow?.postMessage(
      JSON.stringify({
        type: 'setToken',
        payload: localStorage.getItem('token'),
      }),
      '*',
    );

    setTimeout(() => {
      location.href = location.href.replace('ton.place', 'tonplace.io');
    }, 2000);
  };
}

export function* reloadAccountsWorker() {
  try {
    const tokenInfo = yield call(getToken);
    if (!tokenInfo.userId) {
      return;
    }

    const accountTokens = yield call(getAccounts);
    if (accountTokens.length > 1) {
      const { users, accounts } = yield call(Api.post, '/main/accounts', {
        accounts: accountTokens.map((a) => ({
          ...a,
          updatedAt: Math.ceil(a.updatedAt / 1000),
        })),
      });
      yield put(mainActions.setUsers(users));
      yield put(mainActions.setAccounts(accounts));
    }
  } catch (e) {}
}

function* changeAccountWorker(action: ChangeAccountAction) {
  yield put(toastActions.setToastLoading());
  yield call(changeAccount, action.payload);
  location.reload();
  // yield put(resetAllStates());
  // yield initWorker();
  // yield put(toastActions.hideToast());
}

function* authWithPasswordWorker(action: AuthWithPasswordAction) {
  yield put(toastActions.setToastLoading());
  try {
    const data = yield call(Api.post, '/auth/password', {
      ...action.payload,
      device: getDeviceId(),
    });
    yield put(toastActions.hideToast());
    yield call(authHandler, data);
  } catch (e) {
    if (e.code === 'incorrect_password') {
      yield put(toastActions.setToastFail(t('auth_with_pass_incorrect_pass')));
    } else {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

// function* listenRouterChange() {
//   /* @ts-ignore */
//   router.addListener((from, to) => {
//     store.dispatch(postsActions.sendSeenPosts());
//   });
// }

function* goToCaptchaWorker() {
  try {
    const { authForm } = yield select(authSelector);
    const phone = authForm.phone.replace(/[^0-9]/g, '').trim();

    if (!phone.length) {
      yield put(toastActions.setToastFail('Введите номер телефона'));
      return;
    }

    if (!validatePhone(phone)) {
      yield put(toastActions.setToastFail('Введите корректный номер телефона'));
      return;
    }

    //yield put(authActions.setAuthFormStep('captcha'));
    yield put(authActions.sendAuthCode(''));
  } catch (e) {}
}

function* sendAuthCodeWorker(action: SendAuthCodeAction) {
  try {
    const { authForm } = yield select(authSelector);
    const isConfirmed = yield call(
      _confirm,
      `+${authForm.phoneCode} ${authForm.phone.replace(
        /_/g,
        '',
      )} — это правильный номер?`,
    ); // TODO: set lang)

    if (!isConfirmed) {
      return;
    }

    const phone = authForm.phone.replace(/[^0-9]/g, '').trim();
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/auth', {
      phoneCode: +authForm.phoneCode,
      phone,
      captcha: action.payload,
    });
    yield put(toastActions.hideToast());
    yield put(authActions.setAuthFormStep('code'));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(authActions.setAuthFormStep('phone'));
  }
}

function* validateCodeWorker() {
  try {
    const { authForm } = yield select(authSelector);
    const code = authForm.code.trim();
    const phone = authForm.phone.replace(/[^0-9]/g, '').trim();

    if (code.length < 4) {
      yield put(toastActions.setToastFail('Введите код'));
      return;
    }

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/auth/check_code', {
      phoneCode: +authForm.phoneCode,
      phone,
      code,
      device: getDeviceId(),
    });
    yield put(toastActions.hideToast());
    yield call(statReachGoal, 'sms_code_success_validate');

    if (data.is_new) {
      yield call(setRegistrationToken, data.access_token);
      router.navigate(REGISTRATION);
    } else {
      yield call(setToken, +data.user_id, data.access_token);
      location.href = '/feed';
    }
  } catch (e) {
    yield call(statReachGoal, 'sms_code_fail_validate');
    yield put(toastActions.setToastFail(e.message));
  }
}

function* createAccountWorker(action: CreateAccountAction) {
  try {
    let { firstName, lastName, sex, nextUrl = '/feed' } = action.payload;

    yield put(toastActions.setToastLoading());

    let ref: any = getStorageItem('ref');
    if (ref) {
      try {
        ref = parseInt(ref, 16);
      } catch (e) {
        ref = 0;
      }
    }

    let utm = getUtmLocal();

    if (utm && utm.campaign) {
      yield call(statReachGoal, `utm_${utm.campaign}`);
    }

    if (ref) {
      yield call(statReachGoal, `register_by_ref`, {
        ref,
      });
    }

    yield call(statReachGoal, 'registration');

    try {
      // @ts-ignore
      window.ttq.track('CompleteRegistration');
    } catch (e) {}

    try {
      // @ts-ignore
      window.VK.Goal('lead');
    } catch (e) {}

    const targetId = parseInt(getStorageItem('targetId') || '0', 10);

    const data = yield call(
      Api.post,
      '/registration',
      {
        first_name: firstName,
        last_name: lastName,
        sex,
        country_id: 0,
        city_id: 0,
        photo_id: 0,
        bday: 0,
        bmonth: 0,
        byear: 0,
        utm: utm?.campaign || '',
        utm_source: utm?.source || '',
        visitorId: utm?.visitorId || '',
        extraParam1: utm?.extraParam1 || '',
        extraParam2: utm?.extraParam2 || '',
        extraParam3: utm?.extraParam3 || '',
        device: getDeviceId(),
        ref,
        targetId: targetId > 0 ? targetId : 0, // skip group negative id
        isolated: isIsolated() ? 1 : 0,
      },
      {
        headers: {
          Authorization: yield call(getRegistrationToken),
        },
      },
    );

    yield call(statReachGoal, 'registration_success');

    try {
      // @ts-ignore
      window.fbq('track', 'CompleteRegistration');
    } catch (e) {}

    try {
      const rand = `${Date.now()}${getRandomInt(10000, 99999999)}`;
      document.querySelector(
        '#trafficjunky_pixel',
      )!.innerHTML = `<img id="1000335951_cpa_testing" src="https://ads.trafficjunky.net/tj_ads_pt?a=1000335951&member_id=1004247341&cb=
${rand}&cti=${data.user_id}&ctv=1&ctd=Registration"
width="1" height="1" border="0" />`;
    } catch (e) {}

    yield call(removeRegistrationToken);
    yield call(setToken, +data.user_id, data.access_token);

    let isMedia = false;
    try {
      //const contentPriorityType = localStorage.getItem('contentPriorityType');
      let contentPriorityRes = ContentPriorityType.ADULT;
      // if (contentPriorityType && contentPriorityType !== 'undefined') {
      //   contentPriorityRes = contentPriorityType as ContentPriorityType;
      // } else if (
      //   location.hostname === 'media.ton.place' ||
      //   location.hostname === 'tonplace.io'
      // ) {
      //   contentPriorityRes = ContentPriorityType.OTHER;
      // }
      //
      // if (contentPriorityRes === ContentPriorityType.OTHER) {
      //   isMedia = true;
      // }

      yield call(Api.post, '/profile/content_priority', {
        contentPriority: contentPriorityRes,
      });
    } catch (e) {
      console.log(e);
    }

    const route = router.getState();
    if (
      route.params.target &&
      (!route.params.target.match('registration|auth|quiz') ||
        route.params.target.match('author'))
    ) {
      const parsed = yield router.matchUrl(route.params.target);
      if (parsed && parsed.path !== '/') {
        nextUrl = parsed.path;
      }
    }

    let afterRegAction = getStorageItem('afterRegAction') || 'none';
    if (afterRegAction && afterRegAction !== 'none') {
      yield call(statReachGoal, `after_reg_action_${afterRegAction}`);
    }

    const { authCode } = yield select(promoCodeSelector);

    if (afterRegAction === 'verify') {
      nextUrl = '/verification';
    } else if (isMedia) {
      nextUrl = '/subscription/media';
    } else if (afterRegAction === 'search') {
      nextUrl = '/search';
    } else if (afterRegAction === 'feed') {
      nextUrl = '/feed';
    } else if (afterRegAction === 'im') {
      nextUrl = '/im';
    } else if (afterRegAction === 'paywall') {
      nextUrl = '/subscription/adult';
    } else if (afterRegAction.match(/^model_(\d+)$/)) {
      const m = afterRegAction.match(/^model_(\d+)$/);
      nextUrl = '/id' + m![1];
    } else if (afterRegAction.match(/^story_(\d+)$/)) {
      const m = afterRegAction.match(/^story_(\d+)$/);
      nextUrl = '/l/story/' + m![1];
    } else if (afterRegAction.match(/^live_(\d+)$/)) {
      const liveId = afterRegAction.match(/^live_(\d+)$/);
      nextUrl = '/live/' + liveId![1];
    } else if (nextUrl.substring(0, 19) == '/subscription/adult') {
      nextUrl = '/subscription/adult?open_pay=1';
    } else if (nextUrl.substring(0, 16) == '/maryana_landing') {
      nextUrl = '/maryanaro';
    } else if (
      nextUrl.substring(0, 8) == '/l/adult' ||
      nextUrl.match('quiz') ||
      nextUrl.substring(0, 9) == '/l/story/'
    ) {
      nextUrl = '/feed';
    } else if (nextUrl.substring(0, 10) == '/l/diamond') {
      nextUrl = '/subscription/fiat?category=1';
    } else if (authCode) {
      try {
        yield call(Api.post, `/promo_code/${authCode.id}/activate`, {
          code: authCode.value,
        });
        nextUrl = '/im' + authCode.modelId;
      } catch (e) {
        nextUrl = '/code/' + authCode.id + '?v=' + authCode.value;
      }
    }
    // } else if (!nextUrl.match('author|/s/|battle')) {
    //   nextUrl = '/feed';
    // }

    // if (nextUrl === '/feed') {
    //   if (+data.user_id % 2 == 0) {
    //     nextUrl += '?w=follow_suggestions';
    //   } else {
    //     statReachGoal('without_follow_suggestion');
    //   }
    // }

    // if (+data.user_id % 2 == 0) {
    //   yield call(statReachGoal, 'test_group_1');
    //   nextUrl = '/subscription/adult';
    // } else {
    //   yield call(statReachGoal, 'test_group_2');
    // }

    // if (isNotificationsSupported()) {
    //   window.history.replaceState(null, document.title, nextUrl);
    //   if (nextUrl.indexOf('?') > -1) {
    //     nextUrl += '&w=push_notifications';
    //   } else {
    //     nextUrl += '?w=push_notifications';
    //   }
    // }

    setTimeout(() => {
      location.href = nextUrl;
    }, 2500);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* registrationUploadPhotoWorker(action: RegistrationUploadPhotoAction) {
  try {
    const form = new FormData();
    form.append('file', action.payload);

    const data = yield call(Api.post, '/registration/photo', form, {
      headers: {
        Authorization: yield call(getRegistrationToken),
      },
    });

    yield put(
      authActions.updateRegistraionForm({
        isPhotoUploading: false,
        photoId: +data.photo_id,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(
      authActions.updateRegistraionForm({
        isPhotoUploading: false,
        photo: null,
      }),
    );
  }
}

function* logoutWorker({ payload }: LogoutAction) {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/profile/logout');
    const { currentUserId } = yield select(authSelector);
    yield call(removeAccount, currentUserId);
    yield call(removeToken);
    yield call(removeRegistrationToken);
    if (payload) {
      location.reload();
    } else {
      location.href = '/';
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* authHandler(data, opts: { skipQuerySearch?: boolean; target?: string } = {}) {
  statReachGoal('auth');
  if (data.is_new) {
    removeToken();
    statReachGoal('auth_unregistered');
    setRegistrationToken(data.access_token);

    const route = router.getState();
    let target = location.pathname;
    if (!opts.skipQuerySearch) {
      target += location.search;
    }

    if (opts.target) {
      target = opts.target;
    } else if (
      route.params.target &&
      (!route.params.target.match('auth') || route.params.target.match('author|oauth'))
    ) {
      const parsed = yield router.matchUrl(route.params.target);
      if (parsed) {
        target = parsed.path;
      }
    }

    router.navigate(REGISTRATION, {
      target,
    });
  } else {
    statReachGoal('auth_registered');
    yield call(addAccount, {
      id: data.user_id,
      updatedAt: Date.now(),
      token: data.access_token,
    });
    yield call(setToken, +data.user_id, data.access_token);
    yield initWorker();

    let w = getTargetModal(true) || '';

    // const { isNotifyEnabled } = yield select(mainSelector);
    // const canShowNotifyReq = yield call(canShowNotifyRequest);
    // if (!isNotifyEnabled && canShowNotifyReq && !w) {
    //   w = 'push_notifications';
    // }

    const route = router.getState();
    if (
      route.name === AUTH_REQUIRED ||
      route.name === AUTH ||
      route.name === AUTH_EMAIL ||
      route.name === ADD_ACCOUNT ||
      route.name === GOOGLE_AUTH
    ) {
      let target = route.params.target;
      if (opts.target) {
        target = opts.target;
        // Google auth stores the add-account page url; unwrap its own target
        const parsedTarget = yield router.matchUrl(opts.target);
        if (parsedTarget?.name === ADD_ACCOUNT && parsedTarget.params.target) {
          target = parsedTarget.params.target;
        }
      }

      if (target && (!target.match('auth') || target.match('author|oauth'))) {
        const parsed = yield router.matchUrl(target);
        if (parsed && parsed.name !== MAIN) {
          yield router.navigate(parsed.name, parsed.params, { replace: true });
        } else {
          yield router.navigate(
            FEED,
            {
              ...parsed.params,
              w,
            },
            { replace: true },
          );
        }
      } else {
        yield router.navigate(FEED, { w }, { replace: true });
      }
    }

    if (w) {
      showModal(w);
    }
  }
}

function* googleAuthWorker(action: GoogleAuthAction) {
  try {
    yield put(toastActions.setToastLoading());

    const data = yield call(Api.post, '/auth/google', {
      device: getDeviceId(),
      token: action.payload,
    });

    yield put(toastActions.hideToast());
    const targetUrl = getStorageItem('google_target_url') || undefined;
    if (targetUrl) {
      removeStorageItem('google_target_url');
    }
    yield call(authHandler, data, {
      target: targetUrl,
    });

    statReachGoal('success_google_auth');
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function stringifyParams(raw: Record<string, any>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key in raw) {
    if (raw[key]) result[key] = raw[key] + '';
  }
  return result;
}

function* telegramApiAuth(apiCall: () => any, goal: string) {
  yield put(toastActions.setToastLoading());
  const data = yield call(apiCall);
  yield put(toastActions.hideToast());
  yield call(authHandler, data, { skipQuerySearch: true });
  statReachGoal(goal);
}

function* telegramAuthWorker(action: TelegramAuthAction) {
  try {
    yield* telegramApiAuth(
      () =>
        Api.post('/auth/telegram', {
          device: getDeviceId(),
          bot_id: Number(action.payload.botId),
          params: stringifyParams(action.payload.params),
        }),
      'success_tg_auth',
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* telegramOidcAuthWorker(action: TelegramOidcAuthAction) {
  try {
    yield* telegramApiAuth(
      () =>
        Api.post('/auth/telegram/oidc', {
          id_token: action.payload.id_token,
          device: getDeviceId(),
        }),
      'success_tg_oidc_auth',
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* telegramRequestLinkWorker(action: TelegramRequestLinkAction) {
  try {
    yield put(toastActions.setToastLoading());

    const linkData = yield call(Api.get, '/auth/telegram/link', {
      params: action.payload,
    });

    yield put(toastActions.hideToast());
    yield put(authActions.setTelegramLinkData(linkData));

    while (true) {
      yield delay(2000);

      const result: any = yield call(Api.post, '/auth/telegram/link/check', {
        token: linkData.token,
        nonce: linkData.nonce,
        id: linkData.tokenId,
        device: getDeviceId(),
      });

      if (result.status === 'already_logged') {
        location.href = '/';
        return;
      }

      if (result.status === 'success') {
        yield put(authActions.clearTelegramLinkData());
        yield call(authHandler, result);
        statReachGoal('success_tg_link_auth');
        return;
      }
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* checkSubscriptionLoopWorker() {
  while (true) {
    const data = yield call(Api.get, '/main/init');
    yield delay(3000);

    if (data.hasSubscription) {
      yield put(mainActions.setDecryptKeys(data.decryptKeys));
      yield put(mainActions.setHasSubscription(data.hasSubscription)); // TODO: Legacy
      // break; // TODO
    }
  }
}

function* checkSubscriptionWorker() {
  const checkSubscriptionLoop = yield fork(checkSubscriptionLoopWorker);
  yield take(authActions.stopCheckSubscriptionLoop);
  yield cancel(checkSubscriptionLoop);
}

function* emailAuthWorker(action: EmailAuthAction) {
  try {
    const { email, onDone } = action.payload;
    if (!validateEmail(email)) {
      yield put(toastActions.setToastFail('Invalid email'));
    }

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/auth/email', {
      email: email,
    });
    if (data && data.access_token && data.user_id) {
      yield put(toastActions.hideToast());
      return yield call(authHandler, data);
    }
    yield put(toastActions.hideToast());
    yield call(onDone);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* validateEmailAuthWorker() {
  try {
    const { params } = yield call(router.getState);

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/auth/email/validate', {
      email: params.email,
      token: params.token,
      rand: +params.rand,
      device: getDeviceId(),
    });
    yield put(toastActions.hideToast());
    yield call(authHandler, data);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    setTimeout(() => {
      location.href = '/';
    }, 2000);
  }
}

function* validateEmailCodeWorker(action: ValidateEmailCodeAction) {
  try {
    const { email, code } = action.payload;

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/auth/email/validate', {
      email: email.trim(),
      code: code.trim(),
      device: getDeviceId(),
    });
    yield put(toastActions.hideToast());
    yield call(authHandler, data);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* telegramLinkAuthWorker(action: TelegramLinkAuthAction) {
  yield call(authHandler, action.payload);
}

function* passkeyAuthWorker(action: {
  payload?: { accessToken?: string; userId?: number; isNew?: boolean };
}) {
  const session = action.payload;
  if (!session || typeof session.accessToken !== 'string' || session.accessToken === '')
    return;
  if (typeof session.userId !== 'number' || typeof session.isNew !== 'boolean') return;
  yield call(authHandler, {
    access_token: session.accessToken,
    user_id: session.userId,
    is_new: session.isNew,
  });
  yield call(statReachGoal, 'success_passkey_auth');
}

export function* authSaga() {
  yield all([
    takeLatest(authActions.init, initWorker),
    takeLatest(authActions.changeAccount, changeAccountWorker),
    takeLatest(authActions.authWithPassword, authWithPasswordWorker),
    takeLatest(authActions.checkSubscriptionLoop, checkSubscriptionWorker),
    takeLatest(authActions.sendAuthCode, sendAuthCodeWorker),
    takeLatest(authActions.validateCode, validateCodeWorker),
    takeLatest(authActions.createAccount, createAccountWorker),
    takeLatest(authActions.registrationUploadPhoto, registrationUploadPhotoWorker),
    takeLatest(authActions.logout, logoutWorker),
    takeLatest(authActions.goToCaptcha, goToCaptchaWorker),
    takeLatest(authActions.telegramAuth, telegramAuthWorker),
    takeLatest(authActions.telegramOidcAuth, telegramOidcAuthWorker),
    takeLatest(authActions.telegramRequestLink, telegramRequestLinkWorker),
    takeLatest(authActions.googleAuth, googleAuthWorker),
    takeLatest(authActions.reloadAccounts, reloadAccountsWorker),
    takeLatest(authActions.emailAuth, emailAuthWorker),
    takeLatest(authActions.validateEmailAuth, validateEmailAuthWorker),
    takeLatest(authActions.validateEmailCode, validateEmailCodeWorker),
    takeLatest(authActions.telegramLinkAuth, telegramLinkAuthWorker),
    takeLatest(authActions.passkeyAuth, passkeyAuthWorker),
  ]);
}
